<template>
  <div>
    <a-table
        size="small"
        :columns="columns"
        :data-source="dataSource"
        :loading="loading"
        :pagination="pagination"
        :row-selection="rowSelection"
        :scroll="{ x: 'max-content' }"
        row-key="id"
        :locale="{ emptyText: 'Nenhum pedido encontrado.' }"
        @change="(pagination, filters, sorter) => $emit('change', pagination, filters, sorter)"
    >
        <template slot="id_pedido" slot-scope="record">
            <div class="col-pedido">{{ record.id }}</div>
        </template>

        <template slot="id_cotacao" slot-scope="record">
            <div class="col-cotacao">{{ record.quotation_id }}</div>
        </template>

        <template slot="loja" slot-scope="record">
            <div class="col-loja">
                <strong :title="record.store && record.store.name">{{ record.store ? record.store.name : "--" }}</strong>
                <div v-if="record.store && record.store.cnpj">{{ formatCNPJ(record.store.cnpj) }}</div>
            </div>
        </template>

        <template slot="fornecedor" slot-scope="record">
            <div class="col-fornecedor">
                <strong :title="record.provider ? record.provider.name : ''">
                    {{ record.provider ? truncateText(record.provider.name, 20) : "--" }}
                </strong>
                <div v-if="record.provider && record.provider.cnpj">{{ formatCNPJ(record.provider.cnpj) }}</div>
            </div>
        </template>

        <template slot="vendedor" slot-scope="record">
            <div class="col-vendedor" :title="record.seller && record.seller.name">
                {{ record.seller ? truncateText(record.seller.name, 14) : "--" }}
                <div class="col-vendedor-zap" v-if="record.seller && record.seller.phone">
                    <i class="fab fa-whatsapp col-vendedor-zap-icon"></i>
                    <a
                        class="col-vendedor-zap-link"
                        :href="'https://api.whatsapp.com/send?phone=' + record.seller.phone"
                        target="_blank"
                    >
                        {{ formatPhone(record.seller.phone) }}
                    </a>
                </div>
            </div>
        </template>

        <template slot="condicao" slot-scope="record">
            <div class="col-condicao">{{ (record.delivery && record.delivery.forma) || (record.payment_term && record.payment_term.name) || "--" }}</div>
        </template>

        <template slot="valor" slot-scope="record">
            <span class="col-valor">{{ record.value | currency }}</span>
        </template>

        <template slot="balance" slot-scope="record">
            <span class="col-balance" :class="record.balance > 0 ? 'negative-badge' : 'value-badge'">
                {{ record.balance | currency }}
            </span>
        </template>

        <template slot="status" slot-scope="record">
            <div class="col-status" :title="statusTitle(record)">
                <template v-if="record.status === 'C'">
                    <template v-if="record.status_recebido && ((record.divergencia && record.divergencia.length > 0) || (record.divergencia_produto && record.divergencia_produto.length > 0))">
                        <span class="material-symbols-outlined col-status-icon-error">check_circle_unread</span>
                        <span class="col-status-text col-status-text-error">Divergência</span>
                    </template>
                    <template v-else-if="record.status_recebido">
                        <span class="material-icons-round col-status-icon-ok">check_circle</span>
                        <span class="col-status-text col-status-text-ok">Entregue</span>
                    </template>
                    <template v-else>
                        <span class="material-icons-round col-status-icon-ok">check_circle</span>
                        <span class="col-status-text col-status-text-ok">Confirmado</span>
                    </template>
                </template>
                <template v-else>
                    <span class="material-icons-round col-status-icon-pending">schedule</span>
                    <span class="col-status-text col-status-text-pending">Pendente</span>
                </template>
            </div>
        </template>

        <template slot="erp" slot-scope="record">
            <a-tooltip placement="top">
                <template slot="title">
                    <template v-if="!record.integration">
                        <div>{{ record.vf_integration_obs || "Aguardando" }}</div>
                    </template>
                    <template v-else-if="record.integration.status !== 'INTEGRATED'">
                        <div>{{ record.integration.obs || "Falha de integração" }}</div>
                    </template>
                    <template v-else>
                        <div>Pedido sincronizado</div>
                        <div>ID: {{ record.integration.external_reference }}</div>
                        <div>Data: {{ getDate(record.integration.created_at) }} {{ getTime(record.integration.created_at) }}</div>
                        <div v-if="record.integration.obs">Obs: {{ record.integration.obs }}</div>
                    </template>
                </template>
                <div class="col-erp">
                    <template v-if="!record.integration">
                        <span class="material-icons-round col-status-icon-pending">schedule</span>
                        <span class="col-status-text col-status-text-pending">Pendente</span>
                    </template>
                    <template v-else-if="record.integration.status !== 'INTEGRATED'">
                        <span class="material-icons-round col-status-icon-error">error</span>
                        <span class="col-status-text col-status-text-error">Erro</span>
                    </template>
                    <template v-else-if="!record.integration.obs">
                        <span class="material-icons-round col-status-icon-ok">check_circle</span>
                        <span class="col-status-text col-status-text-ok">Integrado</span>
                    </template>
                    <template v-else>
                        <span class="material-icons-round col-status-icon-warning">warning</span>
                        <span class="col-status-text col-status-text-warning">Integrado</span>
                    </template>
                </div>
            </a-tooltip>
        </template>

        <template slot="whatsapp" slot-scope="record">
            <a
                v-if="record.whatsapp_messages && record.whatsapp_messages.length"
                class="col-wpp col-wpp-link"
                :title="whatsapp(record).hint + ' Clique para ver as mensagens.'"
                @click.stop="openWhatsapp(record)"
            >
                <span class="material-icons-round" :class="'col-wpp-' + whatsapp(record).tone">{{ whatsapp(record).icon }}</span>
                <span class="col-status-text" :class="'col-wpp-' + whatsapp(record).tone">{{ whatsapp(record).label }}</span>
                <span v-if="record.whatsapp_messages.length > 1" class="col-wpp-count">({{ record.whatsapp_messages.length }})</span>
            </a>
            <span v-else class="col-wpp" :title="whatsapp(record).hint">
                <span class="col-status-text col-wpp-muted">{{ whatsapp(record).label }}</span>
            </span>
        </template>

        <template slot="visualizado" slot-scope="record">
            <div v-if="record.seller_viewed_at" class="col-data" title="Primeira vez que o vendedor abriu o pedido">
                <div>{{ getDate(record.seller_viewed_at) }}</div>
                <div>{{ getTime(record.seller_viewed_at) }}</div>
            </div>
            <span v-else class="col-wpp-muted" title="O vendedor ainda não abriu o pedido">Não visualizado</span>
        </template>

        <template slot="data_geracao" slot-scope="record">
            <div class="col-data">
                <div>{{ getDate(record.created_at) }}</div>
                <div>{{ getTime(record.created_at) }}</div>
            </div>
        </template>

        <template slot="data_confirmacao" slot-scope="record">
            <div class="col-data">
                <div>{{ getDate(record.data_confirmacao) }}</div>
                <div>{{ getTime(record.data_confirmacao) }}</div>
            </div>
        </template>

        <template slot="data_entrega" slot-scope="record">
            <div class="col-data">
                <div>{{ getDate(record.delivery && record.delivery.data_entrega) }}</div>
                <div>{{ getTime(record.delivery && record.delivery.data_entrega) }}</div>
            </div>
        </template>

        <template slot="data_recebimento" slot-scope="record">
            <div class="col-data">
                <div>{{ getDate(record.received_date) }}</div>
                <div>{{ getTime(record.received_date) }}</div>
            </div>
        </template>

        <template slot="avaliacao" slot-scope="record">
            <div class="col-avaliacao">{{ record.rating ? record.rating.score : "--" }}</div>
        </template>

        <template slot="acoes" slot-scope="record">
            <slot name="actions" :record="record" />
        </template>
    </a-table>

    <SelectionFooter
        :count="selectionCount"
        item-label="pedidos selecionados"
    >
        <template slot="actions">
            <a-button size="large" @click="$emit('clear-selection')">Limpar seleção</a-button>
            <a-button size="large" :loading="printing" :disabled="printing" @click="$emit('print-selected')">Imprimir</a-button>
            <a-button size="large" :loading="resyncing" :disabled="resyncing" @click="$emit('resync-selected')">
                Reenviar para o ERP
            </a-button>
            <a-button
                v-if="canSendNotification"
                size="large"
                :loading="notifying"
                :disabled="notifying"
                @click="$emit('notify-selected')"
            >
                Reenviar notificação
            </a-button>
            <a-button size="large" type="danger" :disabled="!canCancelOrder" @click="$emit('cancel-selected')">
                {{ selectionCount > 1 ? "Cancelar pedidos" : "Cancelar pedido" }}
            </a-button>
        </template>
    </SelectionFooter>

    <OrderWhatsappModal
        :visible="!!whatsappOrder"
        :order="whatsappOrder"
        @close="whatsappOrder = null"
    />
  </div>
</template>

<script>
import moment from "moment";
import SelectionFooter from "@/components/cliente/monitors/SelectionFooter.vue";
import OrderWhatsappModal from "./OrderWhatsappModal.vue";
import { orderWhatsappStatus } from "./order-whatsapp";

export default {
    name: "OrdersTable",
    components: { SelectionFooter, OrderWhatsappModal },
    props: {
        dataSource: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        pagination: {
            type: [Object, Boolean],
            default: () => ({}),
        },
        rowSelection: {
            type: Object,
            default: undefined,
        },
        canCancelOrder: {
            type: Boolean,
            default: true,
        },
        canSendNotification: {
            type: Boolean,
            default: true,
        },
        printing: {
            type: Boolean,
            default: false,
        },
        resyncing: {
            type: Boolean,
            default: false,
        },
        notifying: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            // Pedido cujas mensagens de WhatsApp estão abertas no modal.
            whatsappOrder: null,
        };
    },
    computed: {
        selectionCount() {
            return (this.rowSelection && this.rowSelection.selectedRowKeys.length) || 0;
        },
        columns() {
            return [
                { title: "ID", width: 70, fixed: "left", scopedSlots: { customRender: "id_pedido" } },
                { title: "Cotação", width: 70, scopedSlots: { customRender: "id_cotacao" } },
                { title: "Loja", width: 140, scopedSlots: { customRender: "loja" } },
                { title: "Fornecedor", width: 150, scopedSlots: { customRender: "fornecedor" } },
                { title: "Vendedor", width: 150, scopedSlots: { customRender: "vendedor" } },
                { title: "Condição", width: 80, scopedSlots: { customRender: "condicao" } },
                { title: "Valor (R$)", width: 60, scopedSlots: { customRender: "valor" } },
                { title: "Economia", key: "balance", width: 60, scopedSlots: { customRender: "balance" } },
                { title: "Status", width: 100, scopedSlots: { customRender: "status" } },
                { title: "Status ERP", width: 100, scopedSlots: { customRender: "erp" } },
                { title: "WhatsApp", width: 100, scopedSlots: { customRender: "whatsapp" } },
                { title: "Visualizado", width: 100, scopedSlots: { customRender: "visualizado" } },
                { title: "Criação", width: 100, scopedSlots: { customRender: "data_geracao" } },
                { title: "Confirmação", width: 100, scopedSlots: { customRender: "data_confirmacao" } },
                { title: "Entrega", width: 100, scopedSlots: { customRender: "data_entrega" } },
                { title: "Recebimento", width: 100, scopedSlots: { customRender: "data_recebimento" } },
                { title: "Avaliação", width: 70, scopedSlots: { customRender: "avaliacao" } },
                { title: "Ações", key: "acoes", width: 50, fixed: "right", scopedSlots: { customRender: "acoes" } },
            ];
        },
    },
    methods: {
        whatsapp: orderWhatsappStatus,
        openWhatsapp(record) {
            this.whatsappOrder = record;
        },
        statusTitle(record) {
            if (record.status !== "C") return "Não confirmado";
            if (record.status_recebido) {
                const hasDivergencia = (record.divergencia && record.divergencia.length > 0) || (record.divergencia_produto && record.divergencia_produto.length > 0);
                return hasDivergencia ? "Pedido recebido com divergência!" : "Pedido recebido!";
            }
            return "Pedido confirmado pelo vendedor mas não recebido";
        },
        formatCNPJ(cnpj) {
            if (!cnpj) return "";
            cnpj = String(cnpj).padStart(14, "0");
            return cnpj
                .replace(/^(\d{2})(\d)/, "$1.$2")
                .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
                .replace(/\.(\d{3})(\d)/, ".$1/$2")
                .replace(/(\d{4})(\d)/, "$1-$2")
                .substring(0, 18);
        },
        formatPhone(phone) {
            if (!phone) return "--";
            const nums = phone.replace(/\D/g, "");
            return nums.length === 11
                ? nums.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3")
                : nums.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
        },
        truncateText(text, length) {
            if (!text) return text;
            return text.length > length ? text.substring(0, length) + "..." : text;
        },
        getDate(date) {
            return date ? moment(date).format("DD/MM/YYYY") : "--";
        },
        getTime(date) {
            return date ? moment(date).format("HH:mm") : "--";
        },
    },
};
</script>

<style lang="scss" scoped>
// Mesmo tamanho de fonte/altura de linha da tabela original em
// src/pages/monitors/tabs/orders/_orders.tab.vue — lá isso vinha de uma
// regra CSS global da página; aqui precisa estar no próprio componente pra
// valer também fora daquela página (ex: winners-v2).
::v-deep(.ant-table-thead > tr > th) {
    font-size: 0.8rem;
}
::v-deep(.ant-table-tbody > tr > td) {
    overflow: visible !important;
    font-size: 0.7rem;
}
::v-deep(.ant-table-tbody > tr:nth-child(odd) > td) {
    background-color: #ffffff;
}
::v-deep(.ant-table-tbody > tr:nth-child(even) > td) {
    background-color: #fafafa;
}
.col-vendedor-zap {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 0.9rem;
}
.col-vendedor-zap-icon {
    color: #4ac959;
}
.col-vendedor-zap-link {
    font-size: 0.7rem;
}
.col-valor,
.col-balance {
    white-space: nowrap;
    word-break: keep-all;
}
.value-badge {
    background: green;
    color: white;
    padding: 4px 10px;
    border-radius: 10px;
    opacity: 0.7;
    overflow: hidden;
    white-space: nowrap;
    word-break: keep-all;
}
.negative-badge {
    background: red;
    color: white;
    padding: 4px 10px;
    border-radius: 10px;
    opacity: 0.7;
    overflow: hidden;
    white-space: nowrap;
    word-break: keep-all;
}
.col-status,
.col-erp {
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
}
.col-status-text {
    font-size: 0.85em;
    font-weight: 500;
}
.col-status-icon-ok {
    color: #30aa4c;
}
.col-status-text-ok {
    color: #4caf50;
}
.col-status-icon-error,
.col-status-text-error {
    color: #cd2a2a;
}
.col-status-icon-pending,
.col-status-text-pending {
    color: orange;
}
.col-status-icon-warning,
.col-status-text-warning {
    color: orange;
}
.col-wpp {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
    .material-icons-round {
        font-size: 1.1rem;
    }
}
.col-wpp-link {
    cursor: pointer;
}
.col-wpp-count {
    color: rgba(0, 0, 0, 0.45);
    font-size: 0.85em;
}
.col-wpp-ok {
    color: #30aa4c;
}
.col-wpp-info {
    color: #1677ff;
}
.col-wpp-neutral {
    color: rgba(0, 0, 0, 0.65);
}
.col-wpp-pending {
    color: orange;
}
.col-wpp-error {
    color: #cd2a2a;
}
.col-wpp-muted {
    color: rgba(0, 0, 0, 0.35);
}
</style>
